import {useState, useEffect, useContext, useCallback, useRef} from 'react';
import DataContext from '@/contexts/DataContext';
import { useComponentLogger } from '@/lib/component-logger';
import { fetchAPIData, replaceParams, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { syncLastUsedParams } from '@/lib/dataHandlers/sync-last-used-params';

import { Box, height } from '@mui/system';
import Stack from '@mui/material/Stack';
import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { MagnifyingGlass as MagnifyingGlassIcon } from '@phosphor-icons/react/dist/ssr/MagnifyingGlass';
import { XCircle as XCircleIcon } from '@phosphor-icons/react/dist/ssr/XCircle';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { useTreeViewApiRef } from '@mui/x-tree-view';


import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

export function TreeForm({ treeObject={}, treeParams={}, setTreeParams=()=>{console.error("missing setTreeParams")},onParentCancel, onParentOK, selectedPointCount, setSelectedPointCount }) {
    const logger = useComponentLogger('Tree Form');
    const { currentProject, currentView, selectedViewParams, setSelectedViewParams, viewParams, changedParams } = useContext(DataContext);
    const [selectedTags, setSelectedTags] = useState([]); // selected tags (from the search bar)
    const [tags, setTags] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]); // nodes selected in the tree, array of id strings
    const [selectedPoints, setSelectedPoints] = useState([]); // selected points (leaves)
    const [filteredNodes, setFilteredNodes] = useState([]); // initialized to treeData when we first fetch the tree data, gets reset when we clear the tags
    const apiRef = useTreeViewApiRef();
    const trackedParamValsRef = useRef({}); // used to track old param vals and detect changes
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

    // allows us to auto select the children on parent select
    const [selectionPropagation, setSelectionPropagation] = useState({
        parents: true,
        descendants: true,
    });

    useEffect( () => {
        let tracked = extractTrackedParams(treeObject.expr);
        logger.debug('tree config', treeObject, treeParams, viewParams, tracked);
        let params = {};
        for (const param of tracked) {
            if (treeParams[param]) params[param] = treeParams[param].value;
        }

        logger.debug('tracked params', params);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = tracked;

        fetchContent().catch((err) => {
            logger.debug('tree api err', err)
        })  
    }, []);

    useEffect(() => {
        fetchContent().catch((err) => {
            logger.debug('tree api err', err)
        })  
    }, [currentProject])

    async function fetchContent() {
        if (!treeObject.expr) return;
        logger.debug('tree api start', currentProject)
        if (currentProject === null || currentProject === undefined) return;
        const expr = treeObject.expr;
        
        const baseURL = `https://data.clistek.io/api/${currentProject.id?.replace("p:","")}/eval/?expr=`;
    
        let formattedExpr = replaceParams(expr, treeParams);
        logger.debug('tree replace', formattedExpr, treeParams)
        const apiData = await fetchAPIData(baseURL, formattedExpr, true); // replace = true bc tree expr may contain "[]"
        logger.debug("tree api data", apiData, baseURL, formattedExpr);
        if (!apiData || apiData.cols === undefined || apiData.rows === undefined || apiData.rows.length === 0) return null;

        const tree = buildTree(apiData);
        logger.debug("tree parsed", tree);
        setTreeData(tree);
        setFilteredNodes(tree);

        let tags = processTags(apiData.cols, 'name');
        logger.debug('tree tags', tags, apiData.cols, apiData.cols[0].name)
        setTags(tags);

    }

    /**
     * Tweaked from https://www.geeksforgeeks.org/javascript/how-to-recursively-build-a-json-tree-structure-in-javascript/ 
     * @param {*} data raw data from the api call
     * @param {*} parentId IMPORTANT: default val must match the SS def for a null parent (e.g. parentId="", null, etc)
     * @returns 
     */
    function buildTree(data, parentId = "") { 
        let tree = [];
        data.rows.forEach(node => {
            // Check if the node belongs to the current parent
            // logger.debug('cur node', node, node.parentId?.val, parentId, node.id?.val);
            if (node.parentId == parentId || node.parentId?.val === parentId) {
            // if (node.parentId?.val === parentId) {
                // logger.debug('node match')
                // Recursively build the children of the current node
                let children = buildTree(data, node.id?.val);
                // logger.debug('tree children', children)
                // If children exist, assign them to the current node
                if (children.length) {
                    node.children = children;
                }
                // nodes should show the dis tag, which is the shortest version of the name (e.g. 'subchild 1' instead of 'parent 1 child 1 subchild1')
                node.treeLabel = node.dis || node.id.dis || null;
                
                // Add the current node to the tree
                if (node.treeLabel !== null) tree.push(node);
            } 
        });

        return tree;
    }


    /**
     * Sorts and filters the searchable tags
     * @param {array of objects} rawTags the cols array from the api data
     * @param {*} property tha property to sort and filter the cols by (e.g. name) 
     * @returns 
     */
    function processTags(rawTags, property) {
        // remove id, parentId and dis from the search
        let filteredTags = rawTags.filter((tag) => 
            tag.name !== 'id' && tag[property] !== 'parentId' && tag.name !== 'dis'
        );

        // sort alphabetically
        let sortedTags = filteredTags.sort((a, b) => {
            const upperA = a[property].toUpperCase();
            const upperB = b[property].toUpperCase();
            return upperA <= upperB ? -1 : 1;
        });

        // so we can group options by their first letter
        // https://mui.com/material-ui/react-autocomplete/#grouped
        let groupedTags = sortedTags.map((option) => {
            const firstLetter = option[property][0].toUpperCase();
            return {
                firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
                ...option,
            };
        });

        return groupedTags;
    }

    /**
     * Filters selectedNodes for selectedPoints (leaves)
     * @param {*} newSelected 
     * @returns 
     */
    const filterSelectedPoints = (newSelected = []) => {
        logger.debug('new pts start', newSelected)
        const filterLeaf = (id) => {
            const node = apiRef.current.getItem(id);
            logger.debug('cur node', id, node, newSelected, filteredNodes, treeData);
            if (node === null || node === undefined) return false;

            let isLeaf = false;
            if (!node.children || node.children.length === 0) isLeaf = true;
            logger.debug('is leaf', isLeaf, node)
            return isLeaf;
        }


        const newPoints = newSelected?.filter(filterLeaf) ?? [];
        logger.debug('new points final', newPoints, newSelected)

        return newPoints;
    }

    const handleSelectedNodesChange = (event, ids) => {
        logger.debug('select change', ids)
        // if ( !ids || ids.length === 0) return;
        setSelectedNodes(ids);
        const newPoints = filterSelectedPoints(ids);
        logger.debug('new points', newPoints);
        setSelectedPoints(newPoints);
        setSelectedPointCount(newPoints?.length ?? 0);
    };

    const handleSelectAllClick = () => {
        // setselectedNodes((oldSelected) =>
        //     oldSelected.length === 0 ? getItemIds() : [],
        // );

        setSelectedNodes((oldSelected) => {
            // const allIds = getNodeIds(treeData); // IDs of all leaf items
            const allIds = getNodeIds(filteredNodes); // IDs of all leaf items
            const newSelected = oldSelected.length === 0 ? allIds : [];

            const newPoints = filterSelectedPoints(newSelected);
            // logger.debug('new points', newPoints, filteredNodes);

            setSelectedPointCount(newPoints.length);
            setSelectedPoints(newPoints);
            // logger.debug('selected points', leafCount, newPoints.length)
            return newSelected;
        });

    };

    const handleNodeSelected = (event, id, isSelected) => {
        const node = apiRef.current.getItem(id);
        // logger.debug('node selected', selectedNodes, isSelected, node);
        
        // we only wanna count the leaves
        if (!node.children) {
            setSelectedPointCount(prevCount => prevCount + (isSelected ? 1 : -1));
        }

    };


    /**
     * Tells the tree what attribute to use as the node id
     * If this func is omitted, looks for 'id' by default
     * @param {*} node 
     * @returns 
     */
    function getNodeId(node) {
        return node.id.val;
    }

    /**
     * Tells the tree what attribute to use as the node id
     * If this func is omitted, looks for 'id' by default
     * @param {*} node 
     * @returns 
     */
    function getNodeLabel(node) {
        return node.treeLabel;
    }

     /**
     * 
     * Docs: "the getItemId function should keep the same JavaScript reference between two renders. Otherwise, the Tree View will re-generate its entire structure.
     * It could be achieved by either defining the prop outside the component scope or by memoizing using the React.useCallback hook if the function reuses something from the component scope."
     * https://mui.com/x/react-tree-view/rich-tree-view/nodes/#item-identifier 
     */
    const getNodeIds = useCallback((data) => {
        const ids = [];
        const registerNodeId = (node) => {
            const id = getNodeId(node);
            ids.push(id); // modify this when using custom id attributes. tree looks for 'id' by default
            node.children?.forEach(registerNodeId);
        };

        data.forEach(registerNodeId);

        return ids;
    }, []);


    const onNewTagSelected = (event, newTags) => {
        setSelectedTags(newTags);
        // logger.debug('new tag', newTags, selectedTags, filteredNodes);

        if (newTags.length > 0) {
            let refilteredNodes = treeData;
            for (const tag of newTags) {
                refilteredNodes = filterTree(refilteredNodes, tag.name);
            }
            // logger.debug('filtered tree', refilteredNodes);
            setFilteredNodes(refilteredNodes);

            // sync selectedNodes with filteredNodes
            const filteredIds = extractIds(refilteredNodes);
            const filteredSelectedNodes = selectedNodes.filter(id => filteredIds.includes(id));
            // logger.debug('filtered ids', filteredIds, filteredSelectedNodes);
            handleSelectedNodesChange(null, filteredSelectedNodes);
        } else {
            setFilteredNodes(treeData); // reset if no tags
        }
    }

    const onChipDelete = (deletedChip) => () => {
        const newTags = selectedTags.filter((tag) => tag.name !== deletedChip.name);
        // logger.debug('chip to delete', deletedChip, newTags);
        setSelectedTags(newTags);

        if (newTags.length > 0) {
            let refilteredNodes = treeData;
            for (const tag of newTags) {
                refilteredNodes = filterTree(refilteredNodes, tag.name);
            }
            // logger.debug('filtered tree', refilteredNodes);
            setFilteredNodes(refilteredNodes);

            // sync selectedNodes with filteredNodes
            const filteredIds = extractIds(refilteredNodes);
            const filteredSelectedNodes = selectedNodes.filter(id => filteredIds.includes(id));
            // logger.debug('filtered ids', filteredIds, filteredSelectedNodes);
            handleSelectedNodesChange(null, filteredSelectedNodes);
        } else {
            setFilteredNodes(treeData); // reset if no tags
        }

        // setSelectedTags((tags) => tags.filter((tag) => tag.name !== deletedChip.name));
    }

    const onClearTags = () => {
        setSelectedTags([]);
        setFilteredNodes(treeData);
    }


    /**
     * Hierarachly filters tree based on tags
     * Called itertively for each tag in selectedTags
     * Searches for the first node that contains the tag and includes its parent nodes and all its children
     * @param {*} nodes 
     * @param {*} tag 
     * @returns 
     */
    const filterTree = (nodes, tag) => {
        // logger.debug('filter tree', nodes, tag);
        return nodes
            .map((node) => {
                const matchesTag = node.hasOwnProperty(tag);

                if (matchesTag) {
                    return {...node,}; // return the whole subtree
                }

                // recursively filter children if node doesn't match
                const filteredChildren = node.children
                    ? filterTree(node.children, tag)
                    : [];

                if (filteredChildren.length > 0) {
                    return {
                        ...node,
                        children: filteredChildren,
                    };
                }

                return null;
            })
            .filter(Boolean); // remove null vals
    };

    /**
     * Extracts a list of ids from the tree
     * Helps us sync the filteredNodes and selectedNodes
     * @param {*} nodes 
     * @returns 
     */
    const extractIds = (nodes) => {
        let ids = [];
        for (const node of nodes) {
            ids.push(node.id.val);
            if (node.children?.length) {
                ids = ids.concat(extractIds(node.children));
            }
        }
        return ids;
    };

    const onCancel  = () => {
        onParentCancel();
    }

    const onOK = () => {
        let newParams = {...treeParams};
        let deletedParams = [];
        if (selectedPoints.length > 0) {
            newParams[treeObject.name || 'points'] = {...treeObject, value: selectedPoints};
        } else {
            delete newParams[treeObject.name || 'points']; // clear points if they were unselected
            const name = treeObject.name ?? 'points'
            deletedParams.push(name)
        } 
        
        logger.debug('set selected tree ok: selectedNodes,', selectedNodes, 'selectedPoints', selectedPoints, 'new treeParams', newParams);
        setTreeParams(newParams);
        const appID = currentView?.mobileAppRef?.val ?? null;
        if (appID) syncLastUsedParams(`lastUsedParams-${appID}`, newParams, deletedParams);

        onParentOK(selectedPoints);
    }

    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, treeParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch, treeParams);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
        }
    
        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!treeParams[param]) {
                setSelectedNodes([]);
                setSelectedPoints([]);
                setSelectedPointCount(0);
                return;
            }
        }
    }, [changedParams]);

    useEffect(() => {
        if (treeParams[treeObject.name]) {
            let pts = treeParams[treeObject.name]?.value ?? [];
            let mapped = pts?.filter((pt) => pt.value)
                            .map((pt) => { // rm '@' from ids
                            logger.debug('cur pt', pt)
                            return pt?.value?.replace('@', '');
                        });
            logger.debug('select pts', pts, mapped, mapped.length, treeParams[treeObject.name])
            if (mapped && mapped.length > 0) handleSelectedNodesChange(null, mapped);
        }
    }, [treeParams, treeData]);

    return (
        <Stack spacing={2}>
            <Box>
                {/* Search field */}
                <Stack direction="row">
                    <Autocomplete
                        multiple
                        id="tags-standard"
                        options={tags}
                        // disableCloseOnSelect
                        // limitTags={4} // limit the amount of tags shown when out of focus
                        renderTags={() => null} // disable the chips inside the text field (we'll add them under the field)
                        disableClearable // disable the X btn to clear chips inside the field
                        value={selectedTags} // control selected vals since we're using our own tags
                        groupBy={(option) => option.firstLetter}
                        getOptionLabel={(option) => option.name}
                        onChange={onNewTagSelected}
                        sx={{width: '100%'}}
                        // highlight the options as you search: https://mui.com/material-ui/react-autocomplete/#highlights
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                variant='standard'
                                placeholder='Filter tags'
                                InputProps={{
                                    ...params.InputProps, // Spread existing InputProps
                                    startAdornment: ( // Or endAdornment
                                    <InputAdornment position="start">
                                        <MagnifyingGlassIcon size={20}/>
                                    </InputAdornment>
                                    ),
                                }}
                            />
                            
                        )}
                        renderOption={(props, option, { inputValue }) => {
                            const { key, ...optionProps } = props;
                            const matches = match(option.name, inputValue, { insideWords: true });
                            const parts = parse(option.name, matches);

                            return (
                            <li key={key} {...optionProps}>
                                <div>
                                {parts.map((part, index) => (
                                    <span
                                    key={index}
                                    style={{
                                        fontWeight: part.highlight ? 700 : 400,
                                    }}
                                    >
                                    {part.text}
                                    </span>
                                ))}
                                </div>
                            </li>
                            );
                        }}
                    />
                    {/* <Button variant='outlined'>Filter</Button> */}
                </Stack>
                {/* Chips */}
                { selectedTags.length > 0 ?
                // follows styling from frontend\src\components\dashboard\jobs\jobs-filters.jsx
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', p: 2 }}>
                    {selectedTags.map((tag) => (
                        <Chip
                            key={`-${tag.name}`}
                            label={
                                <Typography variant="inherit">  
                                    {tag.name}
                                </Typography>
                            }
                            onDelete={onChipDelete(tag)}
                            variant="outlined"
                            color='primary'
                        />
                    ))}
                    {/* btn to delete all chips/tags */}
                    {/* ml: 'auto' keeps x btn right-aligned */}
                    <IconButton aria-label="delete" size="large" onClick={onClearTags} sx={{ ml: 'auto' }}>
                        <XCircleIcon />
                    </IconButton>
                </Stack>
                : <></> }
            </Box>
            <Divider />
            <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                <Button color="primary"  onClick={handleSelectAllClick} >
                    {selectedNodes.length === 0 ? 'Select all' : 'Unselect all'}
                </Button>
            </Box>
            <RichTreeView 
                checkboxSelection 
                multiSelect 
                items={filteredNodes}
                apiRef={apiRef}
                getItemId={getNodeId}
                getItemLabel={getNodeLabel}
                selectionPropagation={selectionPropagation} 
                selectedItems={selectedNodes}
                onSelectedItemsChange={handleSelectedNodesChange}
                // onItemSelectionToggle={handleNodeSelected}
            />
            <Stack direction="row" spacing={3}>
                <Button color='secondary' sx={{ flex: '1 1 auto' }} variant='outlined' onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="primary" sx={{ flex: '1 1 auto', color: 'white' }} variant="contained" onClick={onOK}>
                    Confirm
                </Button>
            </Stack>
        </Stack>
    );

    
}
